/* eslint-disable react/require-default-props -- React deprecó defaultProps en componentes
   de función (avisa por consola en cada render): los valores por defecto van en la firma. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Box, Button, Collapse, IconButton, Slider, Stack, Typography } from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  MusicNote,
  Pause,
  PlayArrow,
  SkipNext,
  SkipPrevious,
  VolumeDown,
  VolumeUp
} from '@mui/icons-material';
import PlayTrackList from 'components/play/music/PlayTrackList';

const VOLUME_KEY = 'otx_play_music_volume';

// Cuánto se AGACHA la música cuando el video del paso trae voz de la entrenadora. Se agacha y
// no se pausa: cortar el ambiente de golpe se siente como si la app se hubiera trabado, y al
// volver habría que dar play otra vez (la política de autoplay no perdona).
const DUCK_FACTOR = 0.25;

const storedVolume = () => {
  try {
    const raw = window.localStorage.getItem(VOLUME_KEY);
    const val = raw === null ? 0.6 : Number(raw);
    return Number.isFinite(val) ? Math.min(1, Math.max(0, val)) : 0.6;
  } catch {
    return 0.6;
  }
};

/**
 * OTRIX Play — reproductor de música relajante para las prácticas de recuperación.
 *
 * Acepta una LISTA de pistas (`tracks`) y las encadena: al terminar una pasa a la siguiente y
 * al llegar al final vuelve a empezar, así el ambiente nunca se corta a mitad de la práctica.
 * Con una sola pista se reinicia sola (el mismo efecto del `loop` de antes).
 *
 * NUNCA arranca solo: la política de autoplay de los navegadores exige un gesto, así que el
 * primer play siempre lo da el usuario. Ese gesto es el que habilita el avance automático:
 * sin él, el `play()` del `onEnded` sería rechazado por el navegador.
 *
 * Con más de una pista muestra la COLA plegable (PlayTrackList): se ve qué suena, qué sigue
 * y se salta a cualquier pista tocándola.
 */
function PlayMusicPlayer({ track = null, tracks = null, compact = false, listOpen = false, duck = false }) {
  const { t } = useTranslation(['play']);
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(storedVolume);
  const [index, setIndex] = useState(0);
  const [showList, setShowList] = useState(listOpen);
  // Marca que el cambio de pista debe seguir sonando (avance automático o salto manual),
  // en contraste con el cambio de LISTA, que siempre deja el player en pausa.
  const resumeRef = useRef(false);

  // `track` (singular) se mantiene como API heredada: el panel admin sigue pasando una pista.
  const list = useMemo(() => {
    if (tracks && tracks.length > 0) return tracks;
    return track ? [track] : [];
  }, [tracks, track]);

  // Identidad de la lista: cambia solo cuando cambian las pistas, no cuando avanza el índice.
  const listKey = list.map((tr) => tr?.id).join('-');

  // Lo que se PERSISTE es siempre la elección del usuario, nunca el valor agachado: si se
  // guardara el ducking, cada práctica con voz dejaría la música un 75% más baja para la
  // siguiente, y en pocas sesiones el volumen guardado se degradaría hasta cero.
  useEffect(() => {
    try {
      window.localStorage.setItem(VOLUME_KEY, String(volume));
    } catch {
      // Sin storage disponible: el volumen simplemente no persiste.
    }
  }, [volume]);

  // Lo que SUENA sí lleva el ducking aplicado encima.
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = duck ? volume * DUCK_FACTOR : volume;
  }, [volume, duck]);

  // Cambio de lista: volver al principio, parar y esperar al gesto del usuario.
  useEffect(() => {
    setIndex(0);
    setPlaying(false);
    resumeRef.current = false;
    if (audioRef.current) audioRef.current.pause();
  }, [listKey]);

  // Cambio de pista dentro de la lista: reanudar solo si veníamos sonando.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !resumeRef.current) return;
    resumeRef.current = false;
    audio.play().then(
      () => setPlaying(true),
      () => setPlaying(false)
    );
  }, [index]);

  useEffect(
    () => () => {
      audioRef.current?.pause();
    },
    []
  );

  if (list.length === 0) return null;

  const current = list[Math.min(index, list.length - 1)];
  const multi = list.length > 1;

  const toggle = async () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
      return;
    }
    try {
      await audio.play();
      setPlaying(true);
    } catch {
      setPlaying(false);
    }
  };

  const skip = (delta) => {
    if (!multi) return;
    resumeRef.current = playing;
    setIndex((i) => (i + delta + list.length) % list.length);
  };

  // Tocar una fila de la cola siempre suena: el clic ES el gesto que pide el navegador.
  const jumpTo = (target) => {
    if (target === index) {
      if (!playing) toggle();
      return;
    }
    resumeRef.current = true;
    setIndex(target);
  };

  const handleEnded = () => {
    const audio = audioRef.current;
    if (!multi) {
      // Una sola pista: reinicio manual (el `loop` nativo se quitó al soportar listas).
      if (!audio) return;
      audio.currentTime = 0;
      audio.play().then(
        () => setPlaying(true),
        () => setPlaying(false)
      );
      return;
    }
    resumeRef.current = true;
    setIndex((i) => (i + 1) % list.length);
  };

  const skipSx = {
    flex: 'none',
    color: 'text.secondary',
    transition: 'color 200ms cubic-bezier(0.32,0.72,0,1), transform 200ms cubic-bezier(0.32,0.72,0,1)',
    '&:hover': { color: 'gold.main' },
    '&:active': { transform: 'scale(0.92)' },
    '&.Mui-disabled': { color: 'text.disabled' }
  };

  return (
    <Box
      sx={{
        px: 1.5,
        py: 1,
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'hairline.main',
        backgroundColor: 'surface.overlay',
        width: '100%'
      }}>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={audioRef} src={current.file_url} preload="none" onEnded={handleEnded} />

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {multi && (
          <IconButton
            size="small"
            onClick={() => skip(-1)}
            aria-label={t('music.previous', { ns: 'play', defaultValue: 'Pista anterior' })}
            sx={skipSx}>
            <SkipPrevious fontSize="small" />
          </IconButton>
        )}

        <IconButton
          size="small"
          onClick={toggle}
          aria-label={playing ? t('music.pause', { ns: 'play', defaultValue: 'Pausar música' }) : t('music.play', { ns: 'play', defaultValue: 'Reproducir música' })}
          sx={{
            flex: 'none',
            color: 'ink.main',
            backgroundColor: 'gold.main',
            transition: 'transform 200ms cubic-bezier(0.32,0.72,0,1)',
            '&:hover': { backgroundColor: 'gold.dark' },
            '&:active': { transform: 'scale(0.95)' },
            '&:focus-visible': { outline: '2px solid', outlineColor: 'gold.main', outlineOffset: 2 }
          }}>
          {playing ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
        </IconButton>

        {multi && (
          <IconButton
            size="small"
            onClick={() => skip(1)}
            aria-label={t('music.next', { ns: 'play', defaultValue: 'Pista siguiente' })}
            sx={skipSx}>
            <SkipNext fontSize="small" />
          </IconButton>
        )}

        <Stack spacing={0} sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <MusicNote sx={{ fontSize: 13, color: 'gold.main', flex: 'none' }} />
            <Typography variant="caption" fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>
              {current.title}
            </Typography>
            {multi && (
              <Typography
                variant="caption"
                sx={{ color: 'text.disabled', fontVariantNumeric: 'tabular-nums', flex: 'none' }}>
                {t('music.position', {
                  ns: 'play',
                  current: index + 1,
                  total: list.length,
                  defaultValue: '{{current}} de {{total}}'
                })}
              </Typography>
            )}
          </Stack>
          {!compact && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ pr: 1 }}>
              <VolumeDown sx={{ fontSize: 14, color: 'text.disabled', flex: 'none' }} />
              <Slider
                size="small"
                value={volume}
                min={0}
                max={1}
                step={0.05}
                onChange={(_, v) => setVolume(Array.isArray(v) ? v[0] : v)}
                aria-label={t('music.volume', { ns: 'play', defaultValue: 'Volumen' })}
                sx={{
                  color: 'gold.main',
                  py: 0.75,
                  '& .MuiSlider-thumb': { width: 10, height: 10 }
                }}
              />
              <VolumeUp sx={{ fontSize: 14, color: 'text.disabled', flex: 'none' }} />
            </Stack>
          )}
        </Stack>
      </Box>

      {/* La cola solo tiene sentido con varias pistas; con una sola no hay nada que listar. */}
      {multi && (
        <>
          <Button
            size="small"
            onClick={() => setShowList((v) => !v)}
            endIcon={showList ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
            aria-expanded={showList}
            sx={{
              mt: 0.5,
              px: 0.75,
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '0.75rem',
              color: 'text.secondary',
              transition: 'color 200ms cubic-bezier(0.32,0.72,0,1)',
              '&:hover': { color: 'gold.main', backgroundColor: 'transparent' },
              '&:active': { transform: 'scale(0.98)' }
            }}>
            {showList
              ? t('music.hideQueue', { ns: 'play', defaultValue: 'Ocultar lista' })
              : t('music.showQueue', { ns: 'play', defaultValue: 'Ver lista' })}
          </Button>

          <Collapse in={showList} unmountOnExit>
            <Box sx={{ pt: 0.5, borderTop: '1px solid', borderColor: 'hairline.main', mt: 0.25 }}>
              <PlayTrackList tracks={list} currentIndex={index} playing={playing} onSelect={jumpTo} />
            </Box>
          </Collapse>
        </>
      )}
    </Box>
  );
}

const trackShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  title: PropTypes.string,
  file_url: PropTypes.string
});

PlayMusicPlayer.propTypes = {
  /** API heredada: una sola pista (panel admin). Se ignora si llega `tracks`. */
  track: trackShape,
  /** Lista completa a reproducir en orden (álbum o categoría). */
  tracks: PropTypes.arrayOf(trackShape),
  compact: PropTypes.bool,
  /** Abre la cola desplegada de entrada (por defecto va plegada). */
  listOpen: PropTypes.bool,
  /** Agacha el volumen mientras habla el video del paso. No persiste el valor agachado. */
  duck: PropTypes.bool
};

export default PlayMusicPlayer;
